import { createFlags } from '@/utils/flags'

export interface ContactPreferenceModel {
  typeCode: number
  typeName: string
  value: 'None' | 'Paper' | 'Email' | 'Sms' | 'Ehf' | number
}

export type ContactMethod = 'email' | 'sms' | 'ehf' | 'paper'

export interface MethodCapability {
  enabled: boolean
  reason?: string
}

export type UserContactMethodCapabilities = Record<ContactMethod, MethodCapability>

export interface ContactPreference {
  typeCode: number
  typeName: string
  enabled: boolean
  selectedMethod: ContactMethod | null
  selectedMethods?: ContactMethod[]
}

export const ContactPreferenceTypeCode = {
  VIRKESAVREGNING: 1,
  TRANSPORTFAKTURA: 2,
  SERVICEAVGIFTFAKTURA: 3,
  VIRKESFAKTURA: 4,
  AARSOPPGAVE_ANDELSKAPITAL: 5,
  ABONNEMENTSFAKTURA: 6,
  UTBYTTE_ANDELSKAPITAL_VOLUM: 7,
  AVTALEDOKUMENTER: 8,
  GENERELL_INFORMASJON: 9,
  ORDINAER_FAKTURA: 10,
  MAALEDOKUMENTER: 11,
} as const

export type ContactPreferenceTypeCode =
  (typeof ContactPreferenceTypeCode)[keyof typeof ContactPreferenceTypeCode]

export const CONTACT_PREFERENCE_TYPES = {
  [ContactPreferenceTypeCode.VIRKESAVREGNING]: 'Virkesavregning',
  [ContactPreferenceTypeCode.TRANSPORTFAKTURA]: 'Transportfaktura',
  [ContactPreferenceTypeCode.SERVICEAVGIFTFAKTURA]: 'Serviceavgiftfaktura',
  [ContactPreferenceTypeCode.VIRKESFAKTURA]: 'Virkesfaktura',
  [ContactPreferenceTypeCode.AARSOPPGAVE_ANDELSKAPITAL]: 'Årsoppgave andelskapital',
  [ContactPreferenceTypeCode.ABONNEMENTSFAKTURA]: 'Abonnementsfaktura',
  [ContactPreferenceTypeCode.UTBYTTE_ANDELSKAPITAL_VOLUM]: 'Utbytte på andelskapital/volum',
  [ContactPreferenceTypeCode.AVTALEDOKUMENTER]: 'Avtaledokumenter',
  [ContactPreferenceTypeCode.GENERELL_INFORMASJON]: 'Generell informasjon',
  [ContactPreferenceTypeCode.ORDINAER_FAKTURA]: 'Ordinær faktura',
  [ContactPreferenceTypeCode.MAALEDOKUMENTER]: 'Måledokumenter',
} as const

export const CONTACT_PREFERENCE_DESCRIPTIONS = {
  1: 'Oppgjør for levert tømmer',
  3: 'Årlig kontigent/serviceavgift fastsatt av årsmøtet',
  5: 'Beholdning og endringer i andelskapital for året',
  6: 'Faktura for Magasinet Skog ol.',
  7: 'Utbytte til utbetaling',
  9: 'Nyheter, invitasjoner til arrangementer og generell informasjon',
  10: 'Gjelder alle fakturaer som ikke er abonnementsfaktura og serviceavgift',
  11: 'Dokumentasjon av innmålinger. Valget her gjelder for alle leverandørnumre med mindre du legger til unntak',
} as const

export const generalInfoContactMethods: ContactMethod[] = ['email', 'sms']
export const otherContactMethods: ContactMethod[] = ['email', 'paper', 'ehf']
export const ehfAllowedTypeCodes: number[] = [
  ContactPreferenceTypeCode.SERVICEAVGIFTFAKTURA,
  ContactPreferenceTypeCode.ABONNEMENTSFAKTURA,
  ContactPreferenceTypeCode.ORDINAER_FAKTURA,
]
export const fakturaTypeCodes: number[] = [
  ContactPreferenceTypeCode.SERVICEAVGIFTFAKTURA,
  ContactPreferenceTypeCode.ABONNEMENTSFAKTURA,
  ContactPreferenceTypeCode.ORDINAER_FAKTURA,
]
export const dokumenterTypeCodes: number[] = [
  ContactPreferenceTypeCode.VIRKESAVREGNING,
  ContactPreferenceTypeCode.AARSOPPGAVE_ANDELSKAPITAL,
  ContactPreferenceTypeCode.UTBYTTE_ANDELSKAPITAL_VOLUM,
  ContactPreferenceTypeCode.MAALEDOKUMENTER,
]

/**
 * Flags descriptor for document delivery methods.
 * Parses any raw backend value — number, numeric string, named string, or
 * comma-separated list — into an immutable bitmask.
 */
export const ContactPreferenceOptions = createFlags({
  None: 0,
  Paper: 1,
  Email: 2,
  Sms: 4,
  Ehf: 8,
})

/** An immutable, parsed delivery-method bitmask. Use `ContactPreferenceOptions.from(raw)` to construct. */
export type ContactPreferenceOptions = ReturnType<typeof ContactPreferenceOptions.from>
